import * as React from 'react';
import { Link } from 'react-router-dom';
import { Plus, Building2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '../core/api-client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import { LoadingState } from '../design-system/components/LoadingState';
import { ErrorState } from '../design-system/components/ErrorState';
import { PageHeader } from '../design-system/components/PageHeader';
import { VISUAL, statusPill, statusDot, type StatusTone } from '../design-system/visual';

interface Tenant {
  id: string;
  slug: string;
  name: string;
  currency: string;
  status: string;
  created_at: string;
  /** Comp / free account (migration 043). */
  billing_exempt?: boolean | null;
  comp_expires_at?: string | null;
}

/** Mirrors backend isCompActive: exempt + (no expiry OR future expiry). */
function tenantIsComped(t: Tenant): boolean {
  if (t.billing_exempt !== true) return false;
  if (t.comp_expires_at == null) return true;
  const exp = new Date(t.comp_expires_at);
  return Number.isNaN(exp.getTime()) ? true : exp.getTime() > Date.now();
}

const STATUS_OPTIONS = ['all', 'active', 'trial', 'suspended', 'cancelled'] as const;
type StatusFilter = (typeof STATUS_OPTIONS)[number];
const PAGE_SIZE = 25;

export function TenantsListPage() {
  const [tenants, setTenants] = React.useState<Tenant[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('all');
  const [page, setPage] = React.useState(0);

  function load() {
    setError(null);
    setTenants(null);
    api<{ tenants: Tenant[] }>('/api/admin/tenants')
      .then((r) => setTenants(r.tenants))
      .catch((e) => setError(String(e.message ?? e)));
  }

  React.useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The whole tenant list is fetched client-side (no server params), so both
  // the status filter and pagination below are accurate over the full dataset.
  const filtered = React.useMemo(() => {
    if (!tenants) return null;
    const q = search.trim().toLowerCase();
    return tenants.filter((t) => {
      const matchesQuery = !q || t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q);
      const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [tenants, search, statusFilter]);

  // Reset to the first page whenever the query or status filter changes.
  React.useEffect(() => { setPage(0); }, [search, statusFilter]);

  const pageCount = Math.max(1, Math.ceil((filtered?.length ?? 0) / PAGE_SIZE));
  const paged = React.useMemo(
    () => (filtered ?? []).slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [filtered, page],
  );

  // KPI strip
  const byStatus = (s: string) => (tenants ?? []).filter((t) => t.status === s).length;
  const total = tenants?.length ?? 0;
  const active = byStatus('active');
  const trial = byStatus('trial');
  const suspended = byStatus('suspended');

  return (
    <div>
      <PageHeader
        eyebrow="Platform"
        title="Tenants"
        description="Every customer workspace running on Worqly."
        actions={
          <Link to="/admin/tenants/new">
            <Button className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              Create tenant
            </Button>
          </Link>
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        {/* KPI strip */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi label="Total" value={total} caption="All-time" />
          <Kpi label="Active" value={active} caption="Paying" tone="success" />
          <Kpi label="Trial" value={trial} caption="In 7-day window" tone="info" />
          <Kpi label="Suspended" value={suspended} caption="Manual action" tone={suspended > 0 ? 'warn' : 'muted'} />
        </section>

        {error && <ErrorState description={error} onRetry={load} />}

        {tenants === null && !error && <LoadingState variant="page" />}

        {tenants && tenants.length > 0 && (
          <>
            {/* Search + status filter */}
            <section className="flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  type="search"
                  placeholder="Search by name or slug…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-10 pl-9 rounded-full bg-card/80 border-border/60 text-[13px]"
                />
              </div>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
                <SelectTrigger className="h-10 w-auto min-w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              {filtered && filtered.length !== tenants.length && (
                <p className="text-[12px] text-muted-foreground">
                  {filtered.length} of {tenants.length}
                </p>
              )}
            </section>

            {/* List */}
            <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
              {filtered && filtered.length === 0 ? (
                <div className="py-14 px-6 text-center">
                  <p className="text-[14px] font-semibold text-foreground">No tenants match your filters</p>
                  <p className="text-[12.5px] text-muted-foreground mt-1">Try a different query or status.</p>
                </div>
              ) : (
                <ul className="divide-y divide-border/40">
                  {paged.map((t) => (
                    <li key={t.id}>
                      <Link
                        to={`/admin/tenants/${t.id}`}
                        className="flex items-center gap-4 px-5 sm:px-6 py-4 hover:bg-muted/40 transition-colors"
                      >
                        <div className="h-11 w-11 rounded-2xl bg-muted/50 ring-1 ring-border/40 flex items-center justify-center shrink-0">
                          <Building2 className="h-4 w-4 text-muted-foreground" strokeWidth={1.7} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="text-[14.5px] font-semibold tracking-[-0.005em] text-foreground truncate">{t.name}</span>
                            <span className="text-[12px] font-mono text-muted-foreground">{t.slug}</span>
                          </div>
                          <p className="text-[12px] text-muted-foreground mt-0.5">
                            {t.currency} · created {new Date(t.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {tenantIsComped(t) && <span className={statusPill('success')}>Comped · Free</span>}
                          <StatusBadge status={t.status} />
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Pagination */}
            {filtered && filtered.length > PAGE_SIZE && (
              <div className="flex items-center justify-between text-[12.5px] text-muted-foreground">
                <span className="tabular-nums">
                  {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm" variant="outline" className="rounded-full h-9 gap-1"
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  >
                    <ChevronLeft className="h-3.5 w-3.5" /> Prev
                  </Button>
                  <span className="tabular-nums px-1">Page {page + 1} of {pageCount}</span>
                  <Button
                    size="sm" variant="outline" className="rounded-full h-9 gap-1"
                    disabled={page + 1 >= pageCount}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {tenants && tenants.length === 0 && (
          <div className={[VISUAL.bento, 'py-14 px-8 text-center max-w-md mx-auto'].join(' ')}>
            <div className="mx-auto mb-5 h-14 w-14 rounded-2xl bg-saffron/15 ring-1 ring-saffron/25 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-saffron-foreground" strokeWidth={1.6} />
            </div>
            <p className="text-[18px] font-semibold tracking-[-0.01em] text-foreground">No tenants yet</p>
            <p className="text-[13.5px] text-muted-foreground mt-1.5 leading-relaxed">
              Create the first workspace and we'll send the owner a magic link to set their password.
            </p>
            <div className="mt-6">
              <Link to="/admin/tenants/new">
                <Button className="rounded-full h-10 px-5 font-semibold gap-1.5">
                  <Plus className="h-3.5 w-3.5" /> Create tenant
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({
  label, value, caption, tone = 'neutral',
}: { label: string; value: number; caption: string; tone?: 'neutral' | 'success' | 'info' | 'warn' | 'muted' }) {
  const dotClass =
    tone === 'success' ? 'bg-success' :
    tone === 'info'    ? 'bg-info' :
    tone === 'warn'    ? 'bg-warning' :
    tone === 'muted'   ? 'bg-muted-foreground/40' :
                         'bg-foreground/40';
  return (
    <div className={[VISUAL.bento, 'p-5'].join(' ')}>
      <div className="flex items-center justify-between mb-2.5">
        <p className={VISUAL.eyebrow}>{label}</p>
        <span aria-hidden className={['size-2 rounded-full', dotClass].join(' ')} />
      </div>
      <p className="font-sans font-light text-[2.2rem] leading-none tracking-[-0.03em] tabular-nums text-foreground">{value}</p>
      <p className="text-[12px] text-muted-foreground mt-2">{caption}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tone: StatusTone = status === 'active' ? 'success' : status === 'trial' ? 'info' : status === 'suspended' ? 'warning' : 'muted';
  return (
    <span className={statusPill(tone)}>
      <span className={statusDot(tone)} />
      {status}
    </span>
  );
}
